import React, { useRef, useEffect, useCallback, useMemo } from 'react';

const linkifyHtml = (htmlString) => {
    if (!htmlString) return '';
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        // Walk over all text nodes
        const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
        const textNodes = [];
        let node;
        while ((node = walker.nextNode())) {
            if (
                node.parentNode &&
                node.parentNode.nodeName.toLowerCase() !== 'a' &&
                node.parentNode.nodeName.toLowerCase() !== 'script' &&
                node.parentNode.nodeName.toLowerCase() !== 'style'
            ) {
                textNodes.push(node);
            }
        }

        const urlRegex = /(https?:\/\/[^\s<]+)/g;

        textNodes.forEach((textNode) => {
            if (urlRegex.test(textNode.nodeValue)) {
                const fragment = document.createDocumentFragment();
                let lastIndex = 0;
                urlRegex.lastIndex = 0;

                textNode.nodeValue.replace(urlRegex, (match, p1, offset) => {
                    if (offset > lastIndex) {
                        fragment.appendChild(document.createTextNode(textNode.nodeValue.slice(lastIndex, offset)));
                    }
                    const a = document.createElement('a');
                    a.href = p1;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    a.textContent = p1;
                    fragment.appendChild(a);
                    lastIndex = offset + match.length;
                    return match;
                });

                if (lastIndex < textNode.nodeValue.length) {
                    fragment.appendChild(document.createTextNode(textNode.nodeValue.slice(lastIndex)));
                }

                if (fragment.childNodes.length > 0) {
                    textNode.parentNode.replaceChild(fragment, textNode);
                }
            }
        });
        return doc.body.innerHTML;
    } catch (e) {
        return htmlString;
    }
};

/**
 * Renders email HTML inside an iframe to completely isolate
 * the email's CSS from the parent application.
 */
const SandboxedEmail = ({ html, className, style }) => {
    const iframeRef = useRef(null);
    const processedHtml = useMemo(() => linkifyHtml(html), [html]);

    const adjustHeight = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (doc && doc.body) {
                const height = doc.body.scrollHeight;
                iframe.style.height = height + 'px';
            }
        } catch (e) {
            // cross-origin guard
        }
    }, []);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const handleLoad = () => {
            adjustHeight();

            // Watch for dynamic content changes (images loading, etc.)
            try {
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (doc && doc.body) {
                    const observer = new ResizeObserver(() => adjustHeight());
                    observer.observe(doc.body);

                    // Also handle images loading after initial render
                    const images = doc.querySelectorAll('img');
                    images.forEach((img) => {
                        if (!img.complete) {
                            img.addEventListener('load', adjustHeight);
                            img.addEventListener('error', adjustHeight);
                        }
                    });

                    return () => observer.disconnect();
                }
            } catch (e) {
                // cross-origin guard
            }
        };

        iframe.addEventListener('load', handleLoad);
        return () => iframe.removeEventListener('load', handleLoad);
    }, [html, adjustHeight]);

    // Wrap the email HTML in a minimal document with base styles
    const srcdoc = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
  html, body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    background: transparent;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    line-height: normal;
    color: #374151;
    font-size: 15px;
  }
  img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
    border-style: none;
  }
  p { margin-top: 0; margin-bottom: 1em; }
  ul, ol { margin-top: 0; margin-bottom: 1em; padding-left: 1.5em; }
  li { margin-bottom: 0.25em; }
  blockquote, .gmail_quote {
    margin: 1em 0;
    padding: 0.25em 0 0.25em 1rem;
    border-left: 3px solid #e5e7eb;
    color: #6b7280;
  }
  a {
    color: #2563eb;
    text-decoration: underline;
  }
  h1, h2, h3, h4, h5, h6 {
    color: #111827;
    margin-top: 1.25em;
    margin-bottom: 0.5em;
    font-weight: 600;
  }
</style>
</head>
<body>${processedHtml || ''}</body>
</html>`;

    return (
        <iframe
            ref={iframeRef}
            srcDoc={srcdoc}
            className={className}
            style={{
                width: '100%',
                border: 'none',
                overflow: 'hidden',
                display: 'block',
                minHeight: '50px',
                ...style,
            }}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            scrolling="no"
            frameBorder="0"
            title="Email content"
        />
    );
};

export default SandboxedEmail;
